import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database";

export type BadgeRuleRow = Tables<"badge_rules">;
export type BadgeRuleType = "new" | "top_pick" | "popular";
export type BadgeEntityType = "event" | "venue";

export type BadgeRuleConfig = {
  enabled: boolean;
  windowHours: number;
  metricWeights: Record<string, number>;
  threshold: number;
};

/** Matches DB seed — used when fetch returns empty (e.g. unauthenticated RLS). */
const SEEDED_BADGE_RULES: BadgeRuleRow[] = [
  {
    id: "seed-new-event",
    badge_type: "new",
    entity_type: "event",
    window_hours: 168,
    metric_weights: {},
    threshold: 0,
    min_threshold: 0,
    calibration_percentile: 0.9,
    enabled: true,
    updated_at: "",
  },
  {
    id: "seed-new-venue",
    badge_type: "new",
    entity_type: "venue",
    window_hours: 720,
    metric_weights: {},
    threshold: 0,
    min_threshold: 0,
    calibration_percentile: 0.9,
    enabled: false,
    updated_at: "",
  },
  {
    id: "seed-top_pick-event",
    badge_type: "top_pick",
    entity_type: "event",
    window_hours: 1008,
    metric_weights: { interest_count: 1, proposal_count: 2 },
    threshold: 3,
    min_threshold: 3,
    calibration_percentile: 0.9,
    enabled: true,
    updated_at: "",
  },
  {
    id: "seed-top_pick-venue",
    badge_type: "top_pick",
    entity_type: "venue",
    window_hours: 1008,
    metric_weights: { interest_count: 1, proposal_count: 2 },
    threshold: 3,
    min_threshold: 3,
    calibration_percentile: 0.9,
    enabled: true,
    updated_at: "",
  },
  {
    id: "seed-popular-event",
    badge_type: "popular",
    entity_type: "event",
    window_hours: 72,
    metric_weights: { interest_count: 1, proposal_count: 2, ticket_velocity: 1 },
    threshold: 2,
    min_threshold: 2,
    calibration_percentile: 0.9,
    enabled: true,
    updated_at: "",
  },
  {
    id: "seed-popular-venue",
    badge_type: "popular",
    entity_type: "venue",
    window_hours: 72,
    metric_weights: { interest_count: 1, proposal_count: 2 },
    threshold: 2,
    min_threshold: 2,
    calibration_percentile: 0.9,
    enabled: true,
    updated_at: "",
  },
];

export const BADGE_RULES_STALE_MS = 60 * 60 * 1000;

export const badgeRulesQueryKey = ["badgeRules"] as const;

let cachedRules: BadgeRuleRow[] | null = null;
let cachedAt = 0;

function normalizeMetricWeights(raw: BadgeRuleRow["metric_weights"]): Record<string, number> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "number") out[key] = value;
  }
  return out;
}

export function getBadgeRule(
  rules: BadgeRuleRow[] | undefined,
  badgeType: BadgeRuleType,
  entityType: BadgeEntityType,
): BadgeRuleConfig | undefined {
  if (!rules?.length) return undefined;

  const row = rules.find(
    (rule) => rule.badge_type === badgeType && rule.entity_type === entityType,
  );
  if (!row) return undefined;

  return {
    enabled: row.enabled,
    windowHours: row.window_hours,
    metricWeights: normalizeMetricWeights(row.metric_weights),
    threshold: row.threshold,
  };
}

export function isEntityNewByRule(
  createdAt: string | null | undefined,
  rule: BadgeRuleConfig | undefined,
): boolean {
  if (!rule?.enabled || !createdAt) return false;

  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;

  return Date.now() - created < rule.windowHours * 60 * 60 * 1000;
}

export async function fetchBadgeRules(): Promise<BadgeRuleRow[]> {
  if (cachedRules && Date.now() - cachedAt < BADGE_RULES_STALE_MS) {
    return cachedRules;
  }

  const { data, error } = await supabase.from("badge_rules").select("*");

  if (error) {
    console.warn("[badgeRules] fetch failed, using seeded defaults", error.message);
    cachedRules = SEEDED_BADGE_RULES;
    cachedAt = Date.now();
    return cachedRules;
  }

  cachedRules = data?.length ? data : SEEDED_BADGE_RULES;
  cachedAt = Date.now();
  return cachedRules;
}

export function useBadgeRules() {
  return useQuery({
    queryKey: badgeRulesQueryKey,
    queryFn: fetchBadgeRules,
    staleTime: BADGE_RULES_STALE_MS,
    gcTime: BADGE_RULES_STALE_MS * 2,
  });
}
